/**
 * The stacked share bar (mock .distbar) + the SPLIT slot-dots. The shares
 * come from the caller (manual %, equal, or the formula's — `formula-pool`);
 * quotas use the same largest-remainder math as the planner engine, so the
 * dots show exactly what the Day timeline will plan.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import type { DraftPoolRow } from '../../../drop-generator.types';
import { gwColor, gwLetter } from './cards/strategy-copy';

/** Largest-remainder quotas — mirrors the engine's splitLargestRemainder. */
export function splitQuotas(weights: number[], total: number): number[] {
  if (weights.length === 0 || total <= 0) return weights.map(() => 0);
  const sum = weights.reduce((a, b) => a + b, 0);
  const exact = weights.map((w) => (w / sum) * total);
  const floors = exact.map(Math.floor);
  let rest = total - floors.reduce((a, b) => a + b, 0);
  const byFraction = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of byFraction) {
    if (rest <= 0) break;
    floors[i] += 1;
    rest -= 1;
  }
  return floors;
}

interface Props {
  pool: DraftPoolRow[];
  /** One share per pool row, any scale — normalized here. */
  shares: number[];
  /** SPLIT only: enabled, unpinned slots to divide. null hides the dots. */
  splitOver: number | null;
}

export const WeightsBar: FC<Props> = ({ pool, shares, splitOver }) => {
  if (pool.length === 0) return null;
  const sum = shares.reduce((a, b) => a + b, 0);
  const quotas = splitOver !== null ? splitQuotas(shares, splitOver) : null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex h-6 overflow-hidden rounded-lg">
        {pool.map((row, i) => (
          <span
            key={row.id}
            className="flex items-center justify-center overflow-hidden whitespace-nowrap text-[10px] font-semibold text-white transition-all duration-300"
            style={{
              width: `${(shares[i] / sum) * 100}%`,
              backgroundColor: gwColor(i),
            }}
          >
            {gwLetter(i)} · {Math.round((shares[i] / sum) * 100)}%
          </span>
        ))}
      </div>
      {splitOver === 0 ? (
        <span className="text-[11px] text-muted-foreground">
          no slots to divide here — each container splits its own day by these shares
        </span>
      ) : quotas ? (
        <span className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
          <span className="flex gap-0.5">
            {quotas.flatMap((q, i) =>
              Array.from({ length: q }, (_, k) => (
                <span
                  key={`${i}-${k}`}
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: gwColor(i) }}
                />
              )),
            )}
          </span>
          <span className="ml-1">
            the day&apos;s {splitOver} drops —{' '}
            {quotas.map((q, i) => `${gwLetter(i)} ${q}`).join(' · ')}
          </span>
        </span>
      ) : (
        <span className="text-[11px] text-muted-foreground">
          the odds each drop draws with when you Generate (a re-sync keeps a draw while its gateway stays usable)
        </span>
      )}
    </div>
  );
};
